import * as z from 'zod';

import { paymentStatus } from '@/_pages/agreements/shared/installments/schemas/agreement-installments-query-schema';
import { paymentMethodQueryLinkerSchema } from '@/_pages/settings/payment-methods/shared/schemas/payment-method-query-linker-schema';

/** API returns nested payment money as either a decimal string or number; keep raw query shape stable. */
export const paymentMoneyQuerySchema = z.union([z.string(), z.number()]).nullable();

export const agreementPaymentPaymentRecordsQuerySchema = z.object({
  id: z.number().nullish(),
  payment_transaction_id: z.number().nullish(),
  agreement_payment_id: z.number().nullish(),
  amount_applied: paymentMoneyQuerySchema,
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
});

export type AgreementPaymentPaymentRecordsQuerySchema = z.infer<typeof agreementPaymentPaymentRecordsQuerySchema>;

/**
 * Agreement payment rows nested under `payment_transaction` / `payment_transactions[]` on invoice includes.
 * Differs from installment `agreementPaymentQuerySchema` (nullable installment, string payment_type).
 */
export const paymentTransactionAgreementPaymentQuerySchema = z.object({
  id: z.number(),
  agreement_installment_id: z.number().nullable(),
  amount: paymentMoneyQuerySchema,
  payment_date: z.string(),
  payment_type: z.string(),
  payment_status: paymentStatus,
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
  deleted_at: z.string().nullish(),
  payment_transaction_id: z.number().nullish(),
  payment_records: agreementPaymentPaymentRecordsQuerySchema.nullish(),
});

export type PaymentTransactionAgreementPaymentQuerySchema = z.infer<
  typeof paymentTransactionAgreementPaymentQuerySchema
>;

/** Nested payment transaction rows under `ar_payment_collection.payment_transactions[]`. */
export const paymentTransactionQuerySchema = z.object({
  id: z.number(),
  client_id: z.number(),
  contact_id: z.number().nullable(),
  provider: z.string(),
  payment_method_type: z.string(),
  payment_method_id: z.number().nullable(),
  amount: paymentMoneyQuerySchema,
  status: paymentStatus,
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
  deleted_at: z.string().nullable().nullish(),
  ar_payment_collection_id: z.number().nullish(),
  payment_method: paymentMethodQueryLinkerSchema.nullish(),
  agreement_payments: z.array(paymentTransactionAgreementPaymentQuerySchema).nullish(),
});

export type PaymentTransactionQuerySchema = z.infer<typeof paymentTransactionQuerySchema>;

export const arPaymentCollectionQuerySchema = z.object({
  id: z.number(),
  client_id: z.number().nullish(),
  contact_id: z.number().nullable().nullish(),
  payment_type: z.string().nullish(),
  payment_date: z.string().nullish(),
  total_tender_taken: paymentMoneyQuerySchema.optional(),
  total_amount_due: paymentMoneyQuerySchema.optional(),
  account_credit_amount: paymentMoneyQuerySchema.optional(),
  account_credit_ledger_id: z.number().nullable().nullish(),
  submitted_by_id: z.number().nullish(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
  deleted_at: z.string().nullable().nullish(),
  payment_transactions: z.array(paymentTransactionQuerySchema).nullish(),
});

export type ArPaymentCollectionQuerySchema = z.infer<typeof arPaymentCollectionQuerySchema>;
